/**
 * Dashboard.jsx — /dashboard
 *
 * Per-user board of all attempts grouped by subject (level + board + subject + category).
 * Each group shows completed papers / total papers in that group plus a list of
 * the user's attempts with quick links back into the viewer.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { apiJson } from './api';
import { useAuth } from './AuthContext';
import { useSEO } from './useSEO';

function formatHMS(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return [h, m, ss].map((n) => String(n).padStart(2, '0')).join(':');
}

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function formatScore(a) {
  if (a.score == null) return '—';
  if (a.totalMarks) {
    const pct = Math.round((a.score / a.totalMarks) * 100);
    return `${a.score}/${a.totalMarks} (${pct}%)`;
  }
  return String(a.score);
}

function groupTitle(g) {
  const bits = [g.board, g.level, g.subject].filter(Boolean);
  let t = bits.join(' ');
  if (g.category) t += ` · ${g.category}`;
  return t || 'Unknown subject';
}

export default function Dashboard() {
  useSEO({
    title: 'My dashboard',
    description: 'Track your past-paper attempts, scores and progress across every subject in one place.',
    canonicalPath: '/dashboard',
  });
  const { user, token, logout } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    apiJson('/api/me/dashboard', { token })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, token]);

  const sortedGroups = useMemo(() => {
    if (!data?.groups) return [];
    // Most recent activity first (max of any attempt's updatedAt within the group)
    return [...data.groups].sort((a, b) => {
      const at = Math.max(...a.attempts.map((x) => Date.parse(x.updatedAt || x.createdAt) || 0), 0);
      const bt = Math.max(...b.attempts.map((x) => Date.parse(x.updatedAt || x.createdAt) || 0), 0);
      return bt - at;
    });
  }, [data]);

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.h1}>My dashboard</h1>
        <div style={styles.headerRight}>
          <span style={styles.subtle}>{user.email}</span>
          <button type="button" onClick={logout} style={styles.logoutBtn}>Log out</button>
        </div>
      </div>

      {loading && <p style={styles.subtle}>Loading…</p>}
      {error && <p style={{ color: '#b91c1c' }}>Error: {error}</p>}

      {!loading && !error && data && (
        <>
          <div style={styles.summary}>
            <SummaryCard label="Subjects" value={sortedGroups.length} />
            <SummaryCard label="Total attempts" value={data.totalAttempts || 0} />
            <SummaryCard label="Submitted" value={data.totalSubmitted || 0} />
          </div>

          {sortedGroups.length === 0 && (
            <div style={styles.empty}>
              You haven't started any papers yet. <Link to="/">Browse papers →</Link>
            </div>
          )}

          {sortedGroups.map((g) => {
            const pct = g.totalPapers > 0 ? Math.round((g.completedPapers / g.totalPapers) * 100) : 0;
            const scored = g.attempts.filter((a) => a.score != null && a.totalMarks);
            const avgPct = scored.length
              ? Math.round(scored.reduce((s, a) => s + (a.score / a.totalMarks) * 100, 0) / scored.length)
              : null;
            return (
              <section key={`${g.level}|${g.board}|${g.subject}|${g.category}`} style={styles.group}>
                <div style={styles.groupHeader}>
                  <h2 style={styles.groupTitle}>{groupTitle(g)}</h2>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {avgPct != null && (
                      <div style={{ ...styles.scorePill, background: '#16a34a' }} title={`Average across ${scored.length} scored attempt${scored.length === 1 ? '' : 's'}`}>
                        Avg {avgPct}%
                      </div>
                    )}
                    <div style={styles.scorePill} title="Distinct papers you've submitted / total papers available">
                      {g.completedPapers}/{g.totalPapers || g.completedPapers} completed
                    </div>
                  </div>
                </div>

                <div style={styles.progressBar}>
                  <div style={{ ...styles.progressFill, width: `${pct}%` }} />
                </div>
                <div style={styles.progressMeta}>
                  {pct}% of available papers · {g.submittedAttempts} submitted attempt{g.submittedAttempts === 1 ? '' : 's'}
                  {g.inProgressAttempts > 0 && ` · ${g.inProgressAttempts} in progress`}
                </div>

                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Paper</th>
                      <th style={styles.th}>Attempt</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Score</th>
                      <th style={styles.th}>Time</th>
                      <th style={styles.th}>Updated</th>
                      <th style={styles.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.attempts.map((a) => (
                      <tr key={a.id} style={styles.tr}>
                        <td style={styles.td}>{a.paper.title}</td>
                        <td style={styles.td}>{a.name}</td>
                        <td style={styles.td}>
                          <span style={{ ...styles.statusBadge, ...statusStyle(a.status) }}>
                            {a.status === 'submitted' ? 'Submitted' : 'In progress'}
                          </span>
                        </td>
                        <td style={styles.td}>{formatScore(a)}</td>
                        <td style={styles.td}>{formatHMS(a.timerSeconds)}</td>
                        <td style={styles.td}>{formatDate(a.updatedAt || a.createdAt)}</td>
                        <td style={styles.td}>
                          <Link to={`/viewer/${a.paperId}`} style={styles.openLink}>Open →</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardValue}>{value}</div>
      <div style={styles.cardLabel}>{label}</div>
    </div>
  );
}

function statusStyle(status) {
  if (status === 'submitted') return { background: 'rgba(22,163,74,0.15)', color: '#15803d', border: '1px solid rgba(22,163,74,0.4)' };
  return { background: 'rgba(234,179,8,0.15)', color: '#a16207', border: '1px solid rgba(234,179,8,0.4)' };
}

const styles = {
  page: { padding: '24px clamp(16px, 4vw, 40px)', maxWidth: 1100, margin: '0 auto', color: 'var(--text)' },
  header: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  logoutBtn: { padding: '6px 14px', borderRadius: 6, border: '1px solid var(--viewer-border, #e5e7eb)', background: 'var(--viewer-surface, #fff)', color: 'var(--text)', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  h1: { margin: 0, fontSize: 28 },
  subtle: { color: 'var(--viewer-muted, #6b7280)', fontSize: 14 },
  summary: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 },
  card: { background: 'var(--viewer-panel, #fff)', border: '1px solid var(--viewer-border, #e5e7eb)', borderRadius: 10, padding: '14px 16px' },
  cardValue: { fontSize: 28, fontWeight: 700, lineHeight: 1.1 },
  cardLabel: { fontSize: 13, color: 'var(--viewer-muted, #6b7280)', marginTop: 4 },
  empty: { padding: 24, background: 'var(--viewer-panel, #fff)', border: '1px dashed var(--viewer-border, #e5e7eb)', borderRadius: 10, textAlign: 'center' },
  group: { background: 'var(--viewer-panel, #fff)', border: '1px solid var(--viewer-border, #e5e7eb)', borderRadius: 12, padding: 16, marginBottom: 16 },
  groupHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 },
  groupTitle: { margin: 0, fontSize: 18 },
  scorePill: { background: 'var(--link, #2563eb)', color: '#fff', padding: '4px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600 },
  progressBar: { height: 8, background: 'var(--viewer-border, #e5e7eb)', borderRadius: 4, overflow: 'hidden' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, #16a34a, #22c55e)', transition: 'width 0.3s' },
  progressMeta: { fontSize: 12, color: 'var(--viewer-muted, #6b7280)', marginTop: 6, marginBottom: 12 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 640 },
  th: { textAlign: 'left', padding: '8px 6px', borderBottom: '1px solid var(--viewer-border, #e5e7eb)', fontSize: 12, color: 'var(--viewer-muted, #6b7280)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 },
  tr: {},
  td: { padding: '8px 6px', borderBottom: '1px solid var(--viewer-border-soft, #f3f4f6)' },
  statusBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600 },
  openLink: { color: 'var(--link, #2563eb)', textDecoration: 'none', fontWeight: 600 },
};
